import type {
  SubmitHandler,
  UseFormSetError,
  UseFormWatch
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../../../mantine/notifications/useToast";
import { setErrorsFromResponseData } from "../../../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { type Contract } from "../../../DeliveryOverview.types";

interface UseContractSubmitParams {
  onSubmit: (contract: Contract) => Promise<void>;
  watch: UseFormWatch<Contract>;
  setError: UseFormSetError<Contract>;
  /**
   * Called with the caught error after field errors have been applied, e.g. to
   * navigate to the tab that contains the erroneous fields.
   */
  onError?: (error: unknown) => void;
}

/**
 * Shared submit handler for the contract create/edit forms. Awaits `onSubmit`
 * and, on failure, maps API response errors onto the corresponding form fields
 * (and toasts unmatched errors).
 */
function useContractSubmit({
  onSubmit,
  watch,
  setError,
  onError
}: UseContractSubmitParams): SubmitHandler<Contract, void> {
  const { showErrorToast } = useToast();
  const { t } = useTranslation();

  return async (data) => {
    try {
      await onSubmit(data);
    } catch (error) {
      setErrorsFromResponseData<Contract>(
        showErrorToast,
        error,
        watch(),
        setError,
        t("errors.UnknownError")
      );

      onError?.(error);
    }
  };
}

export { useContractSubmit, UseContractSubmitParams };
